import {
    OpenFeature,
    ProviderEvents,
    InMemoryProvider,
    ErrorCode,
} from "@openfeature/web-sdk";
import { FlagdWebProvider } from "@openfeature/flagd-web-provider";
import version from "~~/package.json";
import defaultOpenFeatureFlags from '#shared/defaultOpenFeatureFlags.json';

export default defineNuxtPlugin((nuxtApp) => {
    const logger = useLogger("plugins/02.openfeature");
    logger.info("init", OpenFeature.getClient().providerStatus);

    // `OpenFeature` from @openfeature/web-sdk is a process-global singleton. During SSR
    // this plugin runs once per request; registering providers and event handlers here
    // would attach per-request closures (capturing the request-scoped Nuxt/reactive
    // context) onto that global singleton and keep them alive after the response is sent.
    // That is a classic SSR memory leak (see nodejs_memory_leak_analyse.md, Priority 1).
    // The handlers only update client-side reactive state and the browser provider, so
    // we guard all global mutation to the client. On the server we just provide the
    // client so `$openfeature` stays available for SSR reads.
    if (import.meta.server) {
        return {
            provide: {
                openfeature: OpenFeature,
            },
        };
    }

    OpenFeature.clearHandlers();

    const domain = useRuntimeConfig()?.public?.cookieDomain;

    if (
        useRuntimeConfig()?.public?.openfeature.provider === "flagd-web" &&
        !useDevice().isCrawler
    ) {
        let hostURL = useRequestURL();
        try {
            hostURL = new URL(useRuntimeConfig()?.public?.apiUrl);
        } catch (error) { }

        const settings = {
            host: hostURL?.hostname,
            port: Number(useRequestURL()?.port || "443"),
            pathPrefix: "api/flagd",
            tls: hostURL?.protocol === "https:",
        };

        OpenFeature.setProviderAndWait(domain, new FlagdWebProvider(settings));
    } else {
        OpenFeature.setProviderAndWait(domain, new InMemoryProvider(defaultOpenFeatureFlags));
    }

    OpenFeature.addHandler(ProviderEvents.Ready, (eventDetails) => {
        const allKeys = new Set([
            ...Array.from(useFlagsUsedState()?.value ?? []),
            ...Object.keys(defaultOpenFeatureFlags ?? {}),
        ]);

        allKeys.forEach((feat) => {
            if (eventDetails?.providerName?.includes("flagd")) {
                updateFlag(feat, defaultOpenFeatureFlags);
            } else {
                updateFlag(feat, defaultOpenFeatureFlags);
            }
        });
    });

    const refreshFlags = (eventDetails: any) => {
        if (Array.isArray(eventDetails?.flagsChanged)) {
            eventDetails.flagsChanged.forEach((flagName: string) =>
                updateFlag(flagName, defaultOpenFeatureFlags)
            );
        } else {
            const allKeys = new Set([
                ...Array.from(useFlagsUsedState()?.value ?? []),
                ...Object.keys(defaultOpenFeatureFlags ?? {}),
            ]);
            allKeys.forEach((feat) => updateFlag(feat, defaultOpenFeatureFlags));
        }
    };

    OpenFeature.addHandler(ProviderEvents.Reconciling, refreshFlags);
    OpenFeature.addHandler(ProviderEvents.ContextChanged, refreshFlags);
    OpenFeature.addHandler(ProviderEvents.ConfigurationChanged, refreshFlags);
    OpenFeature.addHandler(ProviderEvents.Stale, (_eventDetails) => { });

    onNuxtReady(() => {
        const context: any = useABConfigCookie()?.value?.context ?? {};
        context.targetingKey = useExpCookieIdentifier().value ?? "bt_undefined";
        context.storeid = useStoreCookie().value;
        context.version = version.version;
        context.build = useRuntimeConfig()?.public?.version?.build;
        context.ismobile = nuxtApp?.$device?.isMobile;
        context.useragent = nuxtApp?.$device?.userAgent;
        context.usergroup = useUserGroup()?.value;
        context.lastseenonline = Date.now().toString();

        useABConfigCookie().value = { context };
        OpenFeature.setContext({ ...context });

        watch(
            () => useStoreCookie().value,
            (newVal) => {
                setABContextVariable("storeid", newVal);
                OpenFeature.setContext({
                    ...(useABConfigCookie().value?.context ?? {}),
                    storeid: newVal,
                });
            }
        );

        watch(
            () => useABConfigCookie().value,
            (ol, ct) => {
                const oldContext = { ...(ol?.context ?? {}) };
                const newContext = { ...(ct?.context ?? {}) };
                const ofContext = { ...(OpenFeature.getContext() ?? {}) };

                if (oldContext.storeid === newContext.storeid && newContext.storeid !== ofContext.storeid) {
                    OpenFeature.setContext({ ...newContext });
                }
            }
        );
    });

    return {
        provide: {
            openfeature: OpenFeature,
        },
    };
});

/**
 * updateFlag behavior:
 *   - if flag is render-cfg-* and is a universal config object => store FULL object (so UI can resolve per entry)
 *   - else if universal config object => resolve once using provider variant (optional)
 *   - else store raw value
 */
function updateFlag(flagName: string, defaultFlags?: any) {
    const lowerCaseName = (flagName ?? "").toLowerCase();
    const domain = useRuntimeConfig()?.public?.cookieDomain;
    const client = OpenFeature.getClient(domain);

    const ddef =
        defaultFlags?.[lowerCaseName]?.disabled != undefined
            ? defaultFlags?.[lowerCaseName]?.disabled == false
            : false;

    const boolVal = client.getBooleanValue(lowerCaseName, ddef);
    useFeatureState(lowerCaseName).value = boolVal;

    if (lowerCaseName.startsWith("render-cfg-promo-pill-theme")) {
        const fullCfgFromDefaults = defaultFlags?.[lowerCaseName];
        useFeatureValue(lowerCaseName).value = fullCfgFromDefaults;
        try {
            const details = client.getObjectDetails(lowerCaseName, fullCfgFromDefaults);
            useFlagVariants().value.set(lowerCaseName, details?.variant ?? "default");
        } catch {
            useFlagVariants().value.set(lowerCaseName, "default");
        }
        return;
    }

    useFlagVariants().value.set(lowerCaseName, "default");

    try {
        const defEntry = defaultFlags?.[lowerCaseName];
        const defaultRaw =
            defEntry?.variants?.[defEntry?.defaultVariant] ?? defEntry;

        let details = client.getObjectDetails(lowerCaseName, defaultRaw);
        if (details?.errorCode === ErrorCode.TYPE_MISMATCH)
            details = client.getNumberDetails(lowerCaseName, defaultRaw);
        if (details?.errorCode === ErrorCode.TYPE_MISMATCH)
            details = client.getStringDetails(lowerCaseName, defaultRaw);
        if (details?.errorCode === ErrorCode.TYPE_MISMATCH)
            details = client.getBooleanDetails(lowerCaseName, defaultRaw);

        useFeatureValue(lowerCaseName).value = details.value;
        useFlagVariants().value.set(lowerCaseName, details?.variant ?? "default");

        if (details?.errorCode === ErrorCode.TYPE_MISMATCH) {
            useFeatureValue(lowerCaseName).value = boolVal;
            useFlagVariants().value.set(lowerCaseName, "default");
        }
    } catch (error) {
        useFeatureValue(lowerCaseName).value = boolVal;
        useFlagVariants().value.set(lowerCaseName, "default");
    }
}
